import { useState, useCallback } from "react";
import { LogIn, Mail, Trash2, Download, RefreshCw, Users, ShieldCheck, Eye, EyeOff, LogOut, Copy, Check, MessageSquare, CheckCircle, RotateCcw } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Intl.DateTimeFormat("en-IN", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit", hour12: true,
      timeZone: "Asia/Kolkata",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

const TABS = [
  { id: "signups",   label: "Pre-Registrations", icon: Users },
  { id: "deletions", label: "Deletion Requests", icon: Trash2 },
  { id: "messages",  label: "Messages",          icon: MessageSquare },
];

export default function AdminPage() {
  const [key, setKey] = useState("");
  const [showKey, setShowKey] = useState(false);
  const [authed, setAuthed] = useState(false);
  const [authError, setAuthError] = useState("");
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [tab, setTab] = useState("signups");
  const [signups, setSignups] = useState([]);
  const [deletions, setDeletions] = useState([]);
  const [messages, setMessages] = useState([]);
  const [deleteTarget, setDeleteTarget] = useState(null); // { ...entry, _type: 'signup'|'deletion' }
  const [copied, setCopied] = useState(false);
  const [search, setSearch] = useState("");

  const fetchAll = useCallback(async (adminKey) => {
    setRefreshing(true);
    try {
      const k = encodeURIComponent(adminKey);
      const [r1, r2, r3] = await Promise.all([
        fetch(`${API}/api/beta/admin/emails?key=${k}`),
        fetch(`${API}/api/delete-request/admin?key=${k}`),
        fetch(`${API}/api/contact/admin/messages?key=${k}`),
      ]);
      if (!r1.ok || !r2.ok || !r3.ok) throw new Error("Unauthorized");
      const [d1, d2, d3] = await Promise.all([r1.json(), r2.json(), r3.json()]);
      setSignups(d1.emails || []);
      setDeletions(d2.requests || []);
      setMessages(d3.messages || []);
      setAuthed(true);
    } catch {
      setAuthError("Incorrect admin key. Try again.");
      setAuthed(false);
    } finally {
      setRefreshing(false);
      setLoading(false);
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setAuthError("");
    setLoading(true);
    await fetchAll(key);
  };

  const handleRemove = async () => {
    if (!deleteTarget) return;
    const k = encodeURIComponent(key);
    try {
      if (deleteTarget._type === "signup") {
        await fetch(`${API}/api/beta/admin/emails/${deleteTarget.id}?key=${k}`, { method: "DELETE" });
        setSignups((prev) => prev.filter((e) => e.id !== deleteTarget.id));
      } else {
        await fetch(`${API}/api/delete-request/admin/${deleteTarget.id}?key=${k}`, { method: "DELETE" });
        setDeletions((prev) => prev.filter((e) => e.id !== deleteTarget.id));
      }
    } catch {
      alert("Failed to delete.");
    }
    setDeleteTarget(null);
  };

  const toggleDeletionStatus = async (req) => {
    const next = req.status === "processed" ? "pending" : "processed";
    try {
      const res = await fetch(
        `${API}/api/delete-request/admin/${req.id}?key=${encodeURIComponent(key)}&status=${next}`,
        { method: "PATCH" }
      );
      if (!res.ok) throw new Error();
      setDeletions((prev) => prev.map((r) => (r.id === req.id ? { ...r, status: next } : r)));
    } catch {
      alert("Failed to update status.");
    }
  };

  const q = search.toLowerCase();
  const filteredSignups = signups.filter((e) => e.email.toLowerCase().includes(q));
  const filteredDeletions = deletions.filter(
    (e) => e.email.toLowerCase().includes(q) || (e.username || "").toLowerCase().includes(q) || (e.reason || "").toLowerCase().includes(q)
  );
  const filteredMessages = messages.filter(
    (e) => (e.name || "").toLowerCase().includes(q) || (e.email || "").toLowerCase().includes(q) || (e.message || "").toLowerCase().includes(q)
  );

  const pendingDeletions = deletions.filter((d) => d.status !== "processed").length;

  const exportCSV = () => {
    let rows;
    let name;
    if (tab === "signups") {
      rows = [["Email", "Timestamp"], ...filteredSignups.map((e) => [e.email, e.timestamp])];
      name = "fanible_preregistrations";
    } else if (tab === "deletions") {
      rows = [["Email", "Username", "Reason", "Details", "Status", "Timestamp"],
        ...filteredDeletions.map((e) => [e.email, e.username || "", e.reason, e.details || "", e.status, e.timestamp])];
      name = "fanible_deletion_requests";
    } else {
      rows = [["Name", "Email", "Message", "Timestamp"], ...filteredMessages.map((e) => [e.name, e.email, e.message, e.timestamp])];
      name = "fanible_contact_messages";
    }
    const csv = rows.map((r) => r.map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${name}_${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const copyAll = () => {
    const list = tab === "signups" ? filteredSignups : tab === "deletions" ? filteredDeletions : filteredMessages;
    const text = list.map((e) => e.email).join("\n");
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  // ── Login screen ────────────────────────────────────────────────────────────
  if (!authed) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ background: "#0C0E13" }}>
        <div
          className="w-full max-w-sm rounded-3xl overflow-hidden"
          style={{
            background: "linear-gradient(145deg, #111420 0%, #0C0E13 100%)",
            border: "1px solid rgba(59,158,255,0.15)",
            boxShadow: "0 0 80px rgba(59,158,255,0.08), 0 32px 64px rgba(0,0,0,0.5)",
          }}
        >
          <div className="h-px w-full" style={{ background: "linear-gradient(90deg, transparent, rgba(59,158,255,0.6) 40%, rgba(96,189,255,0.9) 50%, rgba(59,158,255,0.6) 60%, transparent)" }} />
          <div className="px-8 py-10">
            <div className="flex flex-col items-center mb-8">
              <div className="w-14 h-14 rounded-2xl bg-[#3B9EFF]/10 border border-[#3B9EFF]/25 flex items-center justify-center mb-4">
                <ShieldCheck size={26} className="text-[#3B9EFF]" />
              </div>
              <h1 className="font-outfit font-black text-white text-xl tracking-tight">Admin Access</h1>
              <p className="text-[#A1A1AA] text-xs mt-1 font-inter">Fanible Admin Panel</p>
            </div>

            <form onSubmit={handleLogin} className="space-y-3">
              <div className="relative">
                <input
                  data-testid="admin-key-input"
                  type={showKey ? "text" : "password"}
                  value={key}
                  onChange={(e) => { setKey(e.target.value); setAuthError(""); }}
                  placeholder="Enter admin key"
                  className="w-full bg-white/[0.05] border border-white/10 rounded-xl pl-4 pr-10 py-3.5 text-white text-sm font-inter placeholder-[#555] outline-none focus:border-[#3B9EFF]/50 transition-all duration-150"
                />
                <button type="button" onClick={() => setShowKey(!showKey)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60 transition-colors">
                  {showKey ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>

              {authError && (
                <p data-testid="admin-auth-error" className="text-red-400 text-xs font-inter px-1">{authError}</p>
              )}

              <button
                data-testid="admin-login-btn"
                type="submit"
                disabled={loading || !key}
                className="w-full flex items-center justify-center gap-2 bg-[#3B9EFF] text-black font-bold text-sm uppercase tracking-wider rounded-xl py-3.5 hover:bg-white transition-colors duration-150 disabled:opacity-50"
              >
                {loading ? (
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                ) : (
                  <><LogIn size={15} /> Access Panel</>
                )}
              </button>
            </form>

            <p className="text-center mt-5">
              <a href="/" className="text-[10px] text-[#A1A1AA] hover:text-[#3B9EFF] transition-colors font-inter">← Back to Fanible</a>
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ── Admin panel ─────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen" style={{ background: "#0C0E13" }}>
      {/* Header */}
      <div className="border-b border-white/5 px-6 md:px-10 py-4 flex items-center justify-between" style={{ background: "#0A0C10" }}>
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-xl bg-[#3B9EFF]/10 border border-[#3B9EFF]/25 flex items-center justify-center">
            <ShieldCheck size={16} className="text-[#3B9EFF]" />
          </div>
          <div>
            <span className="font-outfit font-black text-white text-sm tracking-tight">Fanible Admin</span>
            <span className="text-[#A1A1AA] text-xs ml-2 font-inter">Control Panel</span>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            data-testid="admin-refresh-btn"
            onClick={() => fetchAll(key)}
            className="flex items-center gap-1.5 text-xs text-[#A1A1AA] hover:text-white transition-colors border border-white/8 rounded-lg px-3 py-2"
          >
            <RefreshCw size={13} className={refreshing ? "animate-spin" : ""} /> Refresh
          </button>
          <button
            onClick={() => { setAuthed(false); setSignups([]); setDeletions([]); setMessages([]); setKey(""); }}
            className="flex items-center gap-1.5 text-xs text-[#A1A1AA] hover:text-red-400 transition-colors border border-white/8 rounded-lg px-3 py-2"
          >
            <LogOut size={13} /> Sign out
          </button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 md:px-10 py-8">

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-8">
          {[
            { label: "Pre-Registrations", value: signups.length, icon: Users, color: "#3B9EFF" },
            { label: "Pending Deletions", value: pendingDeletions, icon: Trash2, color: "#ef4444" },
            { label: "Messages", value: messages.length, icon: MessageSquare, color: "#a855f7" },
          ].map(({ label, value, icon: Icon, color }) => (
            <div key={label} className="bg-white/[0.03] border border-white/6 rounded-2xl p-5">
              <div className="flex items-center gap-2 mb-2">
                <Icon size={14} style={{ color }} />
                <span className="text-[10px] uppercase tracking-widest font-bold text-[#A1A1AA]">{label}</span>
              </div>
              <p className="font-outfit font-black text-white text-2xl">{value}</p>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-2 mb-6 overflow-x-auto">
          {TABS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              data-testid={`admin-tab-${id}`}
              onClick={() => { setTab(id); setSearch(""); }}
              className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider rounded-xl px-4 py-2.5 transition-all duration-150 whitespace-nowrap"
              style={
                tab === id
                  ? { background: "rgba(59,158,255,0.15)", color: "#3B9EFF", border: "1px solid rgba(59,158,255,0.35)" }
                  : { background: "rgba(255,255,255,0.03)", color: "#A1A1AA", border: "1px solid rgba(255,255,255,0.08)" }
              }
            >
              <Icon size={13} /> {label}
              {id === "deletions" && pendingDeletions > 0 && (
                <span className="bg-red-500/20 text-red-400 text-[9px] font-black rounded-full px-1.5 py-0.5 leading-none">
                  {pendingDeletions}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Toolbar */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-4">
          <input
            data-testid="admin-search-input"
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search..."
            className="flex-1 w-full bg-white/[0.04] border border-white/8 rounded-xl px-4 py-2.5 text-white text-sm font-inter placeholder-[#555] outline-none focus:border-[#3B9EFF]/40 transition-all duration-150"
          />
          <div className="flex items-center gap-2">
            <button
              data-testid="admin-copy-btn"
              onClick={copyAll}
              className="flex items-center gap-1.5 text-xs font-semibold text-[#A1A1AA] hover:text-white border border-white/8 rounded-xl px-4 py-2.5 transition-colors"
            >
              {copied ? <Check size={13} className="text-green-400" /> : <Copy size={13} />}
              {copied ? "Copied!" : "Copy Emails"}
            </button>
            <button
              data-testid="admin-export-btn"
              onClick={exportCSV}
              className="flex items-center gap-1.5 text-xs font-bold text-black bg-[#3B9EFF] hover:bg-white rounded-xl px-4 py-2.5 transition-colors"
            >
              <Download size={13} /> Export CSV
            </button>
          </div>
        </div>

        {/* ── Signups table ── */}
        {tab === "signups" && (
          <div className="rounded-2xl overflow-hidden" style={{ border: "1px solid rgba(255,255,255,0.06)" }}>
            <div className="grid grid-cols-[1fr_auto_auto] gap-4 px-5 py-3 bg-white/[0.03] border-b border-white/5">
              <span className="text-[10px] uppercase tracking-widest font-bold text-[#A1A1AA]">Gmail Address</span>
              <span className="text-[10px] uppercase tracking-widest font-bold text-[#A1A1AA]">Submitted At (IST)</span>
              <span className="text-[10px] uppercase tracking-widest font-bold text-[#A1A1AA]"></span>
            </div>
            {filteredSignups.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <Mail size={32} className="text-white/10 mb-3" />
                <p className="text-[#A1A1AA] text-sm font-inter">{search ? "No emails match your search." : "No pre-registrations yet."}</p>
              </div>
            ) : (
              <div className="divide-y divide-white/[0.04]">
                {filteredSignups.map((entry, i) => (
                  <div key={entry.id} data-testid={`admin-email-row-${i}`}
                    className="grid grid-cols-[1fr_auto_auto] gap-4 items-center px-5 py-4 hover:bg-white/[0.02] transition-colors group">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-8 h-8 rounded-full bg-[#3B9EFF]/10 flex items-center justify-center flex-shrink-0">
                        <span className="text-[#3B9EFF] font-outfit font-black text-xs">{entry.email[0].toUpperCase()}</span>
                      </div>
                      <span className="text-white text-sm font-inter truncate">{entry.email}</span>
                    </div>
                    <span className="text-[#A1A1AA] text-xs font-inter whitespace-nowrap">{formatDate(entry.timestamp)}</span>
                    <button data-testid={`admin-delete-btn-${i}`} onClick={() => setDeleteTarget({ ...entry, _type: "signup" })}
                      className="opacity-0 group-hover:opacity-100 w-7 h-7 rounded-lg bg-red-500/10 hover:bg-red-500/20 flex items-center justify-center transition-all duration-150">
                      <Trash2 size={13} className="text-red-400" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── Deletion requests table ── */}
        {tab === "deletions" && (
          <div className="rounded-2xl overflow-hidden" style={{ border: "1px solid rgba(255,255,255,0.06)" }}>
            <div className="grid grid-cols-[1.2fr_1fr_auto_auto_auto] gap-4 px-5 py-3 bg-white/[0.03] border-b border-white/5">
              <span className="text-[10px] uppercase tracking-widest font-bold text-[#A1A1AA]">Account</span>
              <span className="text-[10px] uppercase tracking-widest font-bold text-[#A1A1AA]">Reason</span>
              <span className="text-[10px] uppercase tracking-widest font-bold text-[#A1A1AA]">Submitted (IST)</span>
              <span className="text-[10px] uppercase tracking-widest font-bold text-[#A1A1AA]">Status</span>
              <span></span>
            </div>
            {filteredDeletions.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <Trash2 size={32} className="text-white/10 mb-3" />
                <p className="text-[#A1A1AA] text-sm font-inter">{search ? "No requests match your search." : "No deletion requests yet."}</p>
              </div>
            ) : (
              <div className="divide-y divide-white/[0.04]">
                {filteredDeletions.map((entry, i) => (
                  <div key={entry.id} data-testid={`admin-deletion-row-${i}`}
                    className="grid grid-cols-[1.2fr_1fr_auto_auto_auto] gap-4 items-center px-5 py-4 hover:bg-white/[0.02] transition-colors group">
                    <div className="min-w-0">
                      <div className="text-white text-sm font-inter truncate">{entry.email}</div>
                      {entry.username && <div className="text-[#A1A1AA] text-[10px] font-inter">@{entry.username}</div>}
                      {entry.details && <div className="text-[#A1A1AA] text-[10px] font-inter truncate mt-0.5 italic">"{entry.details}"</div>}
                    </div>
                    <span className="text-[#A1A1AA] text-xs font-inter truncate">{entry.reason}</span>
                    <span className="text-[#A1A1AA] text-xs font-inter whitespace-nowrap">{formatDate(entry.timestamp)}</span>
                    <button
                      data-testid={`admin-deletion-status-${i}`}
                      onClick={() => toggleDeletionStatus(entry)}
                      className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-wider rounded-full px-3 py-1.5 transition-colors"
                      style={
                        entry.status === "processed"
                          ? { background: "rgba(16,185,129,0.12)", color: "#10b981", border: "1px solid rgba(16,185,129,0.3)" }
                          : { background: "rgba(245,158,11,0.12)", color: "#f59e0b", border: "1px solid rgba(245,158,11,0.3)" }
                      }
                    >
                      {entry.status === "processed" ? <><CheckCircle size={11} /> Processed</> : <><RotateCcw size={11} /> Pending</>}
                    </button>
                    <button data-testid={`admin-deletion-delete-${i}`} onClick={() => setDeleteTarget({ ...entry, _type: "deletion" })}
                      className="opacity-0 group-hover:opacity-100 w-7 h-7 rounded-lg bg-red-500/10 hover:bg-red-500/20 flex items-center justify-center transition-all duration-150">
                      <Trash2 size={13} className="text-red-400" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── Messages table ── */}
        {tab === "messages" && (
          <div className="rounded-2xl overflow-hidden" style={{ border: "1px solid rgba(255,255,255,0.06)" }}>
            <div className="grid grid-cols-[1fr_1fr_2fr_auto] gap-4 px-5 py-3 bg-white/[0.03] border-b border-white/5">
              <span className="text-[10px] uppercase tracking-widest font-bold text-[#A1A1AA]">Name</span>
              <span className="text-[10px] uppercase tracking-widest font-bold text-[#A1A1AA]">Email</span>
              <span className="text-[10px] uppercase tracking-widest font-bold text-[#A1A1AA]">Message</span>
              <span className="text-[10px] uppercase tracking-widest font-bold text-[#A1A1AA]">Received (IST)</span>
            </div>
            {filteredMessages.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <MessageSquare size={32} className="text-white/10 mb-3" />
                <p className="text-[#A1A1AA] text-sm font-inter">{search ? "No messages match your search." : "No messages yet."}</p>
              </div>
            ) : (
              <div className="divide-y divide-white/[0.04]">
                {filteredMessages.map((entry, i) => (
                  <div key={entry.id} data-testid={`admin-message-row-${i}`}
                    className="grid grid-cols-[1fr_1fr_2fr_auto] gap-4 items-start px-5 py-4 hover:bg-white/[0.02] transition-colors">
                    <span className="text-white text-sm font-semibold truncate">{entry.name}</span>
                    <span className="text-[#A1A1AA] text-xs font-inter truncate">{entry.email}</span>
                    <span className="text-[#A1A1AA] text-xs font-inter leading-relaxed break-words">{entry.message}</span>
                    <span className="text-[#A1A1AA] text-xs font-inter whitespace-nowrap">{formatDate(entry.timestamp)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Delete confirm dialog */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.7)", backdropFilter: "blur(6px)" }}>
          <div className="w-full max-w-sm rounded-2xl p-6" style={{ background: "#111420", border: "1px solid rgba(239,68,68,0.2)" }}>
            <h3 className="font-outfit font-black text-white text-lg mb-2">
              {deleteTarget._type === "signup" ? "Remove Registration?" : "Remove Deletion Request?"}
            </h3>
            <p className="text-[#A1A1AA] text-sm font-inter mb-5">
              <span className="text-white font-semibold">{deleteTarget.email}</span> will be removed from the list.
            </p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteTarget(null)}
                className="flex-1 border border-white/10 text-[#A1A1AA] hover:text-white rounded-xl py-2.5 text-sm font-semibold transition-colors">
                Cancel
              </button>
              <button data-testid="admin-confirm-delete-btn" onClick={handleRemove}
                className="flex-1 bg-red-500 hover:bg-red-400 text-white rounded-xl py-2.5 text-sm font-bold transition-colors">
                Remove
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
